import { useCallback, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Loader2,
  Ruler,
  ShoppingCart,
  Wind,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getRouteState } from "@/lib/navigation";
import { calculateBannerPrice, formatGBP } from "@/lib/bannerPricing";
import { createShopifyCart } from "@/lib/api";

const BANNER_SHOPIFY_MAP = {
  pvc: { variantId: "57041736696190", handle: "pvc-banner-5ft-x-3ft" },
  mesh: null,
  scaffolding: null,
};

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "srbt1r-qn.myshopify.com";

const BANNER_MATERIALS = [
  { id: "pvc", apiMat: "pvc-440", title: "PVC Banner", short: "All-round indoor / outdoor", description: "Strong, versatile, suitable for a wide range of display uses.", sides: ["single", "double"], icon: Ruler },
  { id: "mesh", apiMat: "mesh", title: "Mesh Banner", short: "Best for windy / exposed sites", description: "Perforated material designed for outdoor wind resistance.", sides: ["single"], icon: Wind },
  { id: "scaffolding", apiMat: "mesh", title: "Scaffolding Banner", short: "Large-format site branding", description: "Heavy-duty for scaffolding, building wraps, and construction.", sides: ["single"], icon: Building2 },
];

const PRESET_SIZES = [
  { label: "100 \u00d7 50 cm", w: "100", h: "50", unit: "cm", wM: 1, hM: 0.5, note: "Small shop / counter" },
  { label: "200 \u00d7 80 cm", w: "200", h: "80", unit: "cm", wM: 2, hM: 0.8, note: "Roadside / fence" },
  { label: "3 \u00d7 1 m", w: "3", h: "1", unit: "m", wM: 3, hM: 1, note: "Event / frontage" },
  { label: "4 \u00d7 1 m", w: "4", h: "1", unit: "m", wM: 4, hM: 1, note: "Large outdoor" },
  { label: "6 \u00d7 1.5 m", w: "6", h: "1.5", unit: "m", wM: 6, hM: 1.5, note: "Scaffold / site wrap" },
];

const FINISH_OPTIONS = [
  { id: "hem-eyelets", label: "Hemmed + eyelets", note: "Standard outdoor fixing", recommended: true },
  { id: "hem-only", label: "Hemmed edges only", note: "Clean indoor display", recommended: false },
  { id: "eyelets-only", label: "Eyelets only", note: "Quick rope / cable tie", recommended: false },
  { id: "advise-me", label: "Advise me", note: "We'll recommend", recommended: false },
];

const USE_CASES = [
  { id: "shop-front", label: "Shop front / retail", suggest: "pvc" },
  { id: "event-promo", label: "Event / promotion", suggest: "pvc" },
  { id: "roadside", label: "Roadside / forecourt", suggest: "pvc" },
  { id: "fence-exposed", label: "Fence / exposed site", suggest: "mesh" },
  { id: "scaffold-site", label: "Scaffold / construction", suggest: "scaffolding" },
  { id: "indoor", label: "Indoor display", suggest: "pvc" },
  { id: "unsure", label: "Not sure yet", suggest: null },
];

// ── Small presentational helpers (replace nested ternaries for readability) ──
const stepCircleCls = (done, active) => {
  if (done) return "bg-brand-gold text-brand-bg";
  if (active) return "border-2 border-brand-gold text-brand-gold bg-brand-gold/5";
  return "border border-white/20 text-brand-text-secondary/50";
};

const summarizeSize = (sizeLabel, dim) => {
  if (sizeLabel) return `Size: ${sizeLabel}.`;
  if (dim) return `Size: ${dim}.`;
  return "";
};

const sidesOptionCls = (disabled, isActive) => {
  if (disabled) return "cursor-not-allowed border-white/[0.05] text-brand-text-secondary/25";
  if (isActive) return "border-brand-gold bg-brand-gold/10 text-brand-gold ring-1 ring-brand-gold/20";
  return "border-white/10 text-brand-text-secondary/65 hover:text-brand-text hover:border-white/25";
};

function buildBannerQuoteHref({ material, sizeLabel, width, height, sizeUnit, sides, finishing, quantity, lamination }) {
  const params = new URLSearchParams();
  params.set("type", "Business Signage");
  const dim = width && height ? `${width}${sizeUnit} \u00d7 ${height}${sizeUnit}` : "";
  if (dim) params.set("dimensions", dim);
  if (quantity) params.set("quantity", String(quantity));
  const parts = [
    `Banner material: ${material.title}.`,
    summarizeSize(sizeLabel, dim),
    `Sides: ${sides === "double" ? "Double-sided" : "Single-sided"}.`,
    `Finishing: ${FINISH_OPTIONS.find((f) => f.id === finishing)?.label || "Advise me"}.`,
    quantity ? `Quantity: ${quantity}.` : "",
    lamination ? "Lamination / extra protection requested." : "",
  ].filter(Boolean);
  params.set("description", parts.join(" "));
  return `/request-quote?${params.toString()}`;
}

function finishToApiParams(finishId) {
  switch (finishId) {
    case "hem-eyelets": return { eyeletId: "standard", reinforcementId: "hem" };
    case "hem-only": return { eyeletId: "none", reinforcementId: "hem" };
    case "eyelets-only": return { eyeletId: "standard", reinforcementId: "none" };
    default: return { eyeletId: "none", reinforcementId: "none" };
  }
}

function StepBadge({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-3" data-testid={`step-badge-${number}`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold transition-colors duration-300 ${stepCircleCls(done, active)}`}>
        {done ? <Check size={14} /> : number}
      </span>
      <span className={`text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors duration-300 ${active || done ? "text-brand-text" : "text-brand-text-secondary/50"}`}>
        {label}
      </span>
    </div>
  );
}

export function BannerConfigurator({ pricingConfig, onQuoteHrefChange }) {
  const location = useLocation();
  const configuratorRef = useRef(null);

  const [sizeMode, setSizeMode] = useState("preset");
  const [presetIndex, setPresetIndex] = useState(null);
  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const [customUnit, setCustomUnit] = useState("cm");
  const [useCase, setUseCase] = useState(null);
  const [materialId, setMaterialId] = useState("pvc");
  const [sides, setSides] = useState("single");
  const [finishing, setFinishing] = useState("hem-eyelets");
  const [lamination, setLamination] = useState(false);
  const [quantity, setQuantity] = useState("1");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const material = useMemo(() => BANNER_MATERIALS.find((m) => m.id === materialId) || BANNER_MATERIALS[0], [materialId]);
  const suggestedMaterialId = useCase ? USE_CASES.find((u) => u.id === useCase)?.suggest : null;
  const activeSides = material.sides.includes(sides) ? sides : material.sides[0];

  const sizeLabel = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.label || "";
    if (sizeMode === "custom" && customW && customH) return `${customW}${customUnit} \u00d7 ${customH}${customUnit}`;
    return "";
  }, [sizeMode, presetIndex, customW, customH, customUnit]);

  const sizeWidth = sizeMode === "preset" && presetIndex !== null ? PRESET_SIZES[presetIndex]?.w : customW;
  const sizeHeight = sizeMode === "preset" && presetIndex !== null ? PRESET_SIZES[presetIndex]?.h : customH;
  const sizeUnit = sizeMode === "preset" && presetIndex !== null ? (PRESET_SIZES[presetIndex]?.unit || "cm") : customUnit;

  const sizeWidthM = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.wM || 0;
    if (!customW) return 0;
    return customUnit === "m" ? parseFloat(customW) : parseFloat(customW) / 100;
  }, [sizeMode, presetIndex, customW, customUnit]);

  const sizeHeightM = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.hM || 0;
    if (!customH) return 0;
    return customUnit === "m" ? parseFloat(customH) : parseFloat(customH) / 100;
  }, [sizeMode, presetIndex, customH, customUnit]);

  const hasSizeSelected = sizeMode === "preset" ? presetIndex !== null : (customW && customH);

  const calc = useCallback((opts) => calculateBannerPrice(opts, pricingConfig), [pricingConfig]);

  const configuratorPrice = useMemo(() => {
    if (!hasSizeSelected || sizeWidthM <= 0 || sizeHeightM <= 0) return null;
    const { eyeletId, reinforcementId } = finishToApiParams(finishing);
    return calculateBannerPrice({
      widthM: sizeWidthM, heightM: sizeHeightM, materialId: material.apiMat,
      eyeletId, reinforcementId, doubleSided: activeSides === "double",
      quantity: Math.max(1, parseInt(quantity, 10) || 1),
    }, pricingConfig);
  }, [hasSizeSelected, sizeWidthM, sizeHeightM, material, finishing, activeSides, quantity, pricingConfig]);

  const presetPrices = useMemo(() => PRESET_SIZES.map((p) => calc({ widthM: p.wM, heightM: p.hM, materialId: material.apiMat, eyeletId: "none", reinforcementId: "none" }).unitPrice), [calc, material]);

  const quoteHref = buildBannerQuoteHref({ material, sizeLabel, width: sizeWidth, height: sizeHeight, sizeUnit, sides: activeSides, finishing, quantity, lamination });

  const shopifyProduct = BANNER_SHOPIFY_MAP[materialId] || null;

  const handleBannerCheckout = async () => {
    if (!shopifyProduct || checkoutLoading) return;
    setCheckoutLoading(true);
    const note = [
      `PVC Banner`, sizeLabel ? `Size: ${sizeLabel}` : null,
      activeSides === "double" ? "Double-sided" : "Single-sided",
      finishing ? `Finish: ${finishing}` : null,
      quantity && quantity !== "1" ? `Qty: ${quantity}` : null,
      lamination ? "Lamination requested" : null,
    ].filter(Boolean).join(" | ");
    try {
      const result = await createShopifyCart({ variantId: shopifyProduct.variantId, quantity: Math.max(1, parseInt(quantity, 10) || 1), handle: shopifyProduct.handle, note });
      window.open(result.checkout_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[BannerConfigurator] cart creation failed, using direct URL:", err);
      window.open(`https://${SHOPIFY_DOMAIN}/products/${shopifyProduct.handle}`, "_blank", "noopener,noreferrer");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleMaterialSelect = (matId) => {
    const mat = BANNER_MATERIALS.find((m) => m.id === matId) || BANNER_MATERIALS[0];
    setMaterialId(mat.id);
    if (!mat.sides.includes(sides)) setSides(mat.sides[0]);
  };

  return (
    <section ref={configuratorRef} className="mt-20" data-testid="banner-configurator-section">
      <div className="mb-4">
        <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Configure your banner</p>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Three steps to your quote</h2>
      </div>

      <div className="flex items-center gap-4 sm:gap-6 mb-10 py-4 border-b border-white/[0.06] overflow-x-auto" data-testid="configurator-steps-bar">
        <StepBadge number={1} label="Size" active={!hasSizeSelected} done={!!hasSizeSelected} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={2} label="Material" active={!!hasSizeSelected} done={!!hasSizeSelected && !!materialId} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={3} label="Finish" active={!!materialId} done={!!finishing} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
        {/* ── Left: Steps ── */}
        <div className="space-y-8">
          {/* STEP 1: Size */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="step-1-size">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">1</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Choose a size</span>
            </div>
            <div className="flex gap-2 mb-6" data-testid="size-mode-toggle">
              <button type="button" onClick={() => setSizeMode("preset")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "preset" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="size-mode-preset">Common sizes</button>
              <button type="button" onClick={() => setSizeMode("custom")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "custom" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="size-mode-custom">Custom size</button>
            </div>
            {sizeMode === "preset" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="preset-sizes-grid">
                {PRESET_SIZES.map((preset, i) => (
                  <button key={preset.label} type="button" onClick={() => setPresetIndex(i)} className={`rounded-sm border px-5 py-4 text-left transition-all duration-200 ${presetIndex === i ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`preset-size-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold ${presetIndex === i ? "text-brand-gold" : "text-brand-text"}`}>{preset.label}</span>
                      <span className={`text-[13px] font-semibold ${presetIndex === i ? "text-brand-gold/70" : "text-brand-text-secondary/40"}`}>{formatGBP(presetPrices[i])}</span>
                    </div>
                    <span className={`block text-[12px] mt-1 ${presetIndex === i ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{preset.note}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-sm border border-white/[0.08] bg-white/[0.015] p-5" data-testid="custom-size-inputs">
                <div className="grid grid-cols-2 gap-4">
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Width</span>
                    <input type="number" min="0" inputMode="decimal" value={customW} onChange={(e) => setCustomW(e.target.value)} placeholder={customUnit === "m" ? "e.g. 3" : "e.g. 300"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="custom-width-input" />
                  </label>
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Height</span>
                    <input type="number" min="0" inputMode="decimal" value={customH} onChange={(e) => setCustomH(e.target.value)} placeholder={customUnit === "m" ? "e.g. 1" : "e.g. 100"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="custom-height-input" />
                  </label>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-[12px] text-brand-text-secondary/50">Unit:</span>
                  <div className="inline-flex rounded-sm border border-white/10 overflow-hidden" data-testid="custom-unit-toggle">
                    {[{ id: "cm", label: "cm" }, { id: "m", label: "metres" }].map((u) => (
                      <button key={u.id} type="button" onClick={() => setCustomUnit(u.id)} className={`px-4 py-2 text-[12px] font-medium transition-all ${customUnit === u.id ? "bg-brand-gold text-brand-bg" : "text-brand-text-secondary/60 hover:text-brand-text"}`} data-testid={`custom-unit-${u.id}`}>{u.label}</button>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-brand-text-secondary/45">We'll confirm exact dimensions when we prepare your quote.</p>
              </div>
            )}
          </div>

          {/* STEP 2: Material */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="step-2-material">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">2</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Pick a material</span>
            </div>
            <div className="mb-6" data-testid="use-case-selector">
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">What's the banner for? <span className="font-normal text-brand-text-secondary/40">(optional)</span></p>
              <div className="flex flex-wrap gap-2">
                {USE_CASES.map((uc) => (
                  <button key={uc.id} type="button" onClick={() => { setUseCase(uc.id === useCase ? null : uc.id); if (uc.suggest && uc.id !== useCase) handleMaterialSelect(uc.suggest); }} className={`rounded-full px-4 py-2 text-[12px] font-medium transition-all duration-200 ${useCase === uc.id ? "bg-brand-gold text-brand-bg" : "border border-white/12 text-brand-text-secondary/65 hover:text-brand-text hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`use-case-${uc.id}`}>{uc.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="material-cards">
              {BANNER_MATERIALS.map((mat) => {
                const isSuggested = suggestedMaterialId === mat.id;
                const isActive = materialId === mat.id;
                return (
                  <button key={mat.id} type="button" onClick={() => handleMaterialSelect(mat.id)} className={`relative rounded-sm border p-5 text-left transition-all duration-200 ${isActive ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`material-${mat.id}`}>
                    {isSuggested && !isActive && (
                      <span className="absolute -top-2.5 right-3 rounded-full bg-brand-gold/20 border border-brand-gold/30 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-gold">Suggested</span>
                    )}
                    <span className={`${isActive ? "text-brand-gold" : "text-brand-text-secondary/60"}`}><mat.icon size={20} /></span>
                    <p className={`mt-3 text-[15px] font-semibold ${isActive ? "text-brand-gold" : "text-brand-text"}`}>{mat.title}</p>
                    <p className={`mt-1 text-[12px] ${isActive ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{mat.short}</p>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[12px] text-brand-text-secondary/50 leading-relaxed">
              {material.description} {suggestedMaterialId && materialId !== suggestedMaterialId && "You can always override our suggestion."}
            </p>
          </div>

          {/* STEP 3: Finish */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="step-3-finish">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">3</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Finishing details</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div data-testid="sides-group">
                <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Sides</p>
                <div className="flex gap-2">
                  {[{ id: "single", label: "Single-sided" }, { id: "double", label: "Double-sided" }].map((opt) => {
                    const disabled = !material.sides.includes(opt.id);
                    const isActive = activeSides === opt.id;
                    return (
                      <button key={opt.id} type="button" disabled={disabled} onClick={() => setSides(opt.id)} className={`rounded-sm border px-4 py-2.5 text-[12px] font-medium transition-all duration-200 ${sidesOptionCls(disabled, isActive)}`} data-testid={`sides-${opt.id}`}>{opt.label}</button>
                    );
                  })}
                </div>
                {material.sides.length === 1 && <p className="mt-2 text-[11px] text-brand-text-secondary/40">{material.title} is single-sided only.</p>}
              </div>
              <div data-testid="quantity-group">
                <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Quantity</p>
                <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-28 bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-2.5 outline-none transition-colors text-[15px]" data-testid="quantity-input" />
              </div>
            </div>
            <div className="mb-6" data-testid="finishing-group">
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Edge finishing</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {FINISH_OPTIONS.map((opt) => (
                  <button key={opt.id} type="button" onClick={() => setFinishing(opt.id)} className={`rounded-sm border px-4 py-3.5 text-left transition-all duration-200 ${finishing === opt.id ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`finishing-${opt.id}`}>
                    <span className={`block text-[13px] font-medium ${finishing === opt.id ? "text-brand-gold" : "text-brand-text"}`}>{opt.label}</span>
                    <span className={`block text-[11px] mt-1 ${finishing === opt.id ? "text-brand-gold/50" : "text-brand-text-secondary/40"}`}>{opt.note}</span>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer rounded-sm border border-white/[0.06] bg-white/[0.015] px-5 py-4 hover:border-white/15 transition-colors" data-testid="lamination-group">
              <input type="checkbox" checked={lamination} onChange={(e) => setLamination(e.target.checked)} className="h-4 w-4 accent-[#CBA052] shrink-0" data-testid="lamination-checkbox" />
              <span>
                <span className="block text-[13px] font-medium text-brand-text">Add lamination / extra protection</span>
                <span className="block text-[11px] text-brand-text-secondary/45 mt-0.5">For high-handling or extended outdoor display.</span>
              </span>
            </label>
          </div>
        </div>

        {/* ── Right: Live Summary (sticky on desktop) ── */}
        <div className="lg:sticky lg:top-28" data-testid="banner-quote-summary">
          <div className="rounded-sm border border-brand-gold/20 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] p-6 md:p-7 shadow-[0_0_24px_rgba(203,160,82,0.04)]">
            <p className="text-[12px] tracking-[0.16em] uppercase font-bold text-brand-gold mb-6">Your Banner Summary</p>
            <div className="space-y-4">
              {[
                { label: "Size", value: sizeLabel || "Select a size above" },
                { label: "Material", value: material.title },
                { label: "Sides", value: activeSides === "double" ? "Double-sided" : "Single-sided" },
                { label: "Finish", value: FINISH_OPTIONS.find((f) => f.id === finishing)?.label || "\u2014" },
                ...(quantity && quantity !== "1" ? [{ label: "Qty", value: quantity }] : []),
                ...(lamination ? [{ label: "Extra", value: "Lamination requested" }] : []),
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3 last:border-0 last:pb-0" data-testid={`summary-${row.label.toLowerCase()}`}>
                  <span className="text-[12px] uppercase tracking-[0.12em] text-brand-text-secondary/55 font-medium">{row.label}</span>
                  <span className={`text-[14px] font-semibold text-right ${row.value.startsWith?.("Select") ? "text-brand-text-secondary/30 font-normal" : "text-brand-text"}`}>{row.value}</span>
                </div>
              ))}
            </div>
            {configuratorPrice && (
              <div className="mt-6 rounded-sm border border-brand-gold/15 bg-brand-gold/[0.04] p-4" data-testid="configurator-live-price">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-brand-text-secondary/50 font-medium">Estimated price</p>
                    <p className="mt-1 text-[28px] font-bold text-brand-gold leading-none">{formatGBP(configuratorPrice.unitPrice)}</p>
                    {configuratorPrice.breakdown.area > 0 && (
                      <p className="mt-1 text-[12px] text-brand-text-secondary/45" data-testid="configurator-price-per-sqm">
                        {formatGBP(Math.round((configuratorPrice.unitPrice / configuratorPrice.breakdown.area) * 100) / 100)}/m&sup2;
                      </p>
                    )}
                    {parseInt(quantity, 10) > 1 && (
                      <p className="mt-1 text-[12px] text-brand-text-secondary/50">
                        {quantity} units = <span className="text-brand-text font-semibold">{formatGBP(configuratorPrice.totalPrice)}</span>
                      </p>
                    )}
                  </div>
                  {configuratorPrice.bulkDiscount > 0 && (
                    <span className="rounded-full bg-green-500/15 border border-green-500/25 px-2.5 py-1 text-[10px] font-bold text-green-400 uppercase tracking-wider">
                      {Math.round(configuratorPrice.bulkDiscount * 100)}% off
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[10px] text-brand-text-secondary/35">Indicative. Final quote confirmed after review.</p>
              </div>
            )}
            <Link to={quoteHref} state={getRouteState(location)} className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-sm bg-brand-gold px-7 py-4 text-[14px] font-bold tracking-wide text-brand-bg transition-all duration-300 hover:bg-brand-gold-hover hover:shadow-[0_0_20px_rgba(203,160,82,0.2)]" data-testid="banner-request-quote-cta">
              Request Banner Quote <ArrowRight size={16} />
            </Link>
            {shopifyProduct && (
              <button type="button" onClick={handleBannerCheckout} disabled={checkoutLoading} className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-sm border border-white/15 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text-secondary transition-all duration-300 hover:border-brand-gold/40 hover:text-brand-text disabled:opacity-60" data-testid="banner-shopify-cta">
                {checkoutLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Preparing...</>
                  : <><ShoppingCart size={14} /> Order Standard PVC Banner via Shopify</>
                }
              </button>
            )}
            <p className="mt-4 text-center text-[12px] text-brand-text-secondary/45" data-testid="banner-configurator-note">
              Not sure on every detail? Send what you know — we'll guide the rest.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
